import { cn } from '@/shared/utils/cn';
import React, { useRef } from 'react';
import { Toggle } from '@/modules/Toggle';

export interface ListItemToggleProps {
  title: string;
  description?: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
  // Drag and drop props
  draggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  dragHandleIcon?: React.ReactNode;
}

export const ListItemToggle: React.FC<ListItemToggleProps> = ({
  title,
  description,
  checked = false,
  disabled = false,
  onChange,
  className,
  draggable = false,
  isDragging = false,
  isDragOver = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  dragHandleIcon,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handler para establecer la imagen del drag como el contenedor completo
  const handleDragStart = (e: React.DragEvent) => {
    if (containerRef.current) {
      // Usar todo el contenedor como imagen del drag
      e.dataTransfer.setDragImage(containerRef.current, 20, 20);
    }
    onDragStart?.(e);
  };

  return (
    <div
      ref={containerRef}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'ui-flex ui-min-h-32 ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6',
        'ui-transition-all ui-duration-200 ui-ease-in-out',
        {
          // Estado de arrastre - fondo blanco sólido y sombra
          'ui-bg-white ui-shadow-04': isDragging,
          // Estado de drag over
          'ui-bg-fill-hover': isDragOver && !isDragging,
          // 'ui-opacity-50': disabled && !isDragging,
          'ui-items-center': !description,
          'ui-items-start': description,
          // Efecto hover
          'hover:ui-bg-fill-hover': !isDragging && !isDragOver && !disabled,
        },
        className
      )}
    >
      {/* Drag Handle - Siempre renderizado cuando hay icono */}
      {dragHandleIcon && (
        <div
          draggable={draggable && !disabled}
          onDragStart={draggable && !disabled ? handleDragStart : undefined}
          onDragEnd={draggable && !disabled ? onDragEnd : undefined}
          className={cn('ui-flex ui-items-center ui-transition-colors', {
            'ui-pt-6': description,
            'ui-cursor-grab active:ui-cursor-grabbing': draggable && !disabled,
          })}
        >
          {dragHandleIcon}
        </div>
      )}

      {/* Toggle usando el componente oficial del sistema de diseño */}
      <div
        className={cn('ui-flex ui-items-center', {
          ui: description,
        })}
      >
        <Toggle checked={checked} disabled={disabled} onChange={onChange} />
      </div>

      {/* Content */}
      <div className="ui-flex-1">
        <div
          className={cn(
            'ui-text-body-sm-regular ui-font-body-sm-medium ui-leading-body-sm-regular ui-tracking-body-sm',
            {
              // 'ui-mb-4': description,
              'ui-text-neutral-800': isDragging,
              'ui-text-disabled': disabled && !isDragging,
            }
          )}
        >
          {title}
        </div>
        {description && (
          <div
            className={cn(
              'ui-line-clamp-2 ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
              {
                'ui-text-neutral-500': isDragging,
                'ui-text-secondary': !isDragging,
              }
            )}
          >
            {description}
          </div>
        )}
      </div>
    </div>
  );
};
